import { useEffect, useState, useCallback } from "react";
import { Landmark, Plus, X, Trash2, Pencil, CreditCard, ArrowDownLeft, ArrowUpRight, FileSpreadsheet, FileText, Printer, MessageCircle, Mail } from "lucide-react";
import api from "../../lib/api";
import { errText } from "../../lib/errors";
import { toast } from "sonner";
import { downloadApi, printApiPdf } from "../../lib/download";

const tl = (n) => (n ?? 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const inputCls = "h-11 px-4 rounded-xl border border-stone-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-forest/30 w-full";
const ACCOUNT_TYPES = [{ v: "banka", l: "Banka Hesabı" }, { v: "kredi_karti", l: "Kredi Kartı" }, { v: "pos", l: "POS / Sanal POS" }, { v: "kasa", l: "Kasa (Nakit)" }];
const TYPE_LBL = Object.fromEntries(ACCOUNT_TYPES.map((t) => [t.v, t.l]));
const EMPTY_ACC = { name: "", bank_name: "", iban: "", account_type: "banka", commission_rate: 0 };

export default function AdminBank({ types, title = "Banka Hesapları", subtitle = "Banka ve POS hesap bakiyeleri, tahsilat/ödeme işlemleri ve komisyon takibi." }) {
  const allowed = types || ["banka", "pos"];
  const typeOptions = ACCOUNT_TYPES.filter((t) => allowed.includes(t.v));
  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState(null);
  const [open, setOpen] = useState(null); // {account, txns}
  const [caris, setCaris] = useState([]);
  const [txn, setTxn] = useState({ type: "tahsilat", amount: "", description: "", cari_id: "", date: "", installments: 1 });
  const [editTxn, setEditTxn] = useState(null);
  const [busy, setBusy] = useState(false);

  const delTxn = async (t) => {
    if (!window.confirm("İşlem silinsin mi? Hesap bakiyesi ve bağlı cari hareketi otomatik düzeltilir.")) return;
    try { await api.delete(`/admin/bank-transactions/${t.txn_id}`); toast.success("İşlem silindi — bakiye düzeltildi"); openTxns(open.account); load(); }
    catch (e) { toast.error(errText(e) || "Silinemedi"); }
  };

  const saveEditTxn = async () => {
    const amount = parseFloat(editTxn.amount);
    if (!amount || amount <= 0) { toast.error("Geçerli tutar girin"); return; }
    setBusy(true);
    try {
      await api.put(`/admin/bank-transactions/${editTxn.txn_id}`, { type: editTxn.type, amount, description: editTxn.description || "", date: editTxn.date || "", cari_id: editTxn.cari_id || "" });
      toast.success("İşlem güncellendi — bakiye ve cari düzeltildi");
      setEditTxn(null);
      openTxns(open.account);
      load();
    } catch (e) { toast.error(errText(e) || "Güncellenemedi"); }
    finally { setBusy(false); }
  };

  const load = useCallback(async () => {
    try {
      const [{ data: accs }, { data: cl }] = await Promise.all([
        api.get("/admin/bank-accounts"),
        api.get("/admin/cari"),
      ]);
      setAccounts(accs);
      setCaris(cl);
    } catch { toast.error("Hesaplar yüklenemedi"); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openTxns = async (a) => {
    try {
      const { data } = await api.get(`/admin/bank-accounts/${a.account_id}/transactions`);
      setOpen({ account: a, txns: data });
      setTxn({ type: "tahsilat", amount: "", description: "", cari_id: "", date: "", installments: 1 });
    } catch { toast.error("Hareketler yüklenemedi"); }
  };

  const saveAccount = async () => {
    if (!form.name.trim()) { toast.error("Hesap adı zorunlu"); return; }
    setBusy(true);
    try {
      await api.post("/admin/bank-accounts", { ...form, commission_rate: parseFloat(form.commission_rate) || 0 });
      toast.success("Hesap eklendi");
      setForm(null);
      load();
    } catch (e) { toast.error(errText(e) || "Kaydedilemedi"); }
    finally { setBusy(false); }
  };

  const removeAccount = async (a) => {
    if (!window.confirm(`"${a.name}" hesabı silinsin mi? (Bakiyesi sıfır olmalı)`)) return;
    try {
      await api.delete(`/admin/bank-accounts/${a.account_id}`);
      toast.success("Hesap silindi");
      load();
    } catch (e) { toast.error(errText(e) || "Silinemedi"); }
  };

  const downloadStmt = (fmt) => {
    const url = fmt === "xlsx" ? `/admin/reports/banka.xlsx?account_id=${open.account.account_id}` : `/admin/bank-accounts/${open.account.account_id}/statement.pdf`;
    return downloadApi(url, `banka-ekstre.${fmt}`).catch((e) => toast.error(e.message || "Ekstre oluşturulamadı"));
  };

  const printStmt = () => printApiPdf(`/admin/bank-accounts/${open.account.account_id}/statement.pdf`).catch((e) => toast.error(e.message || "Yazdırılamadı"));

  const shareStmt = () => {
    const text = encodeURIComponent(`${open.account.name} hesap özeti — Bakiye: ${tl(open.account.balance)} ₺`);
    window.open(`https://wa.me/?text=${text}`, "_blank");
  };

  const mailStmt = () => {
    const a = open.account;
    const body = encodeURIComponent(`${a.name} — Hesap Özeti\nTür: ${TYPE_LBL[a.account_type]}\nGüncel Bakiye: ${tl(a.balance)} ₺\nHareket sayısı: ${open.txns.length}\n\nDetaylı ekstre PDF/Excel olarak eklenebilir.`);
    window.location.href = `mailto:?subject=${encodeURIComponent(`Hesap Ekstresi — ${a.name}`)}&body=${body}`;
  };

  const [impOpen, setImpOpen] = useState(false);
  const [imp, setImp] = useState(null);
  const [impBusy, setImpBusy] = useState(false);

  const doImport = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f || !open) return;
    const fd = new FormData();
    fd.append("file", f);
    setImpBusy(true);
    try {
      const { data } = await api.post(`/admin/bank-accounts/${open.account.account_id}/import`, fd);
      setImp(data);
      if (!data.rows.length) toast.warning("Dosyada geçerli satır bulunamadı");
    } catch (err2) { toast.error(errText(err2) || "Dosya okunamadı"); }
    finally { setImpBusy(false); }
  };

  const confirmImport = async () => {
    setImpBusy(true);
    try {
      const { data } = await api.post(`/admin/bank-accounts/${open.account.account_id}/import/confirm`, { rows: imp.rows.filter((r) => r.status === "yeni") });
      toast.success(`${data.added} işlem aktarıldı${data.skipped_duplicates ? `, ${data.skipped_duplicates} mükerrer atlandı` : ""}`);
      setImpOpen(false); setImp(null);
      openTxns(open.account);
      load();
    } catch (err2) { toast.error(errText(err2) || "Aktarım başarısız"); }
    finally { setImpBusy(false); }
  };

  const submitTxn = async () => {
    if (!open) return;
    const amount = parseFloat(txn.amount);
    if (!amount || amount <= 0) { toast.error("Geçerli bir tutar girin"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/bank-accounts/${open.account.account_id}/transaction`, { ...txn, amount, installments: parseInt(txn.installments) || 1 });
      toast.success("İşlem kaydedildi");
      openTxns({ ...open.account, balance: data.balance });
      load();
    } catch (e) { toast.error(errText(e) || "İşlem kaydedilemedi"); }
    finally { setBusy(false); }
  };

  const commission = open && txn.type === "tahsilat" && open.account.commission_rate > 0 && parseFloat(txn.amount) > 0
    ? Math.round(parseFloat(txn.amount) * open.account.commission_rate) / 100 : 0;
  const totalBalance = accounts.filter((a) => allowed.includes(a.account_type)).reduce((s, a) => s + (a.balance || 0), 0);

  return (
    <div data-testid="admin-bank-page" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-serif text-2xl sm:text-3xl text-forest">{title}</h1>
          <p className="text-stone-500 text-sm mt-1">{subtitle}</p>
        </div>
        <button data-testid="bank-new-account-button" onClick={() => setForm({ ...EMPTY_ACC, account_type: allowed[0] })} className="flex items-center gap-2 bg-forest hover:bg-forest-dark text-white text-sm font-semibold px-4 py-2.5 rounded-xl transition-colors">
          <Plus className="w-4 h-4" /> Yeni Hesap
        </button>
      </div>

      <div data-testid="bank-total-balance" className="bg-forest text-cream rounded-2xl p-5 shadow-sm max-w-sm">
        <p className="text-xs uppercase tracking-wide text-cream/60">Toplam Bakiye</p>
        <p className="text-3xl font-bold mt-1">{tl(totalBalance)} ₺</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {accounts.filter((a) => allowed.includes(a.account_type)).map((a) => (
          <div key={a.account_id} data-testid={`bank-account-card-${a.account_id}`} className="bg-white rounded-2xl border border-stone-200/80 p-5 shadow-sm">
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2.5">
                {a.account_type === "kredi_karti" ? <CreditCard className="w-5 h-5 text-gold" /> : <Landmark className="w-5 h-5 text-forest" />}
                <div>
                  <p className="font-semibold text-stone-900">{a.name}</p>
                  <p className="text-xs text-stone-400">{a.bank_name || TYPE_LBL[a.account_type]}</p>
                </div>
              </div>
              <button data-testid={`bank-delete-${a.account_id}`} onClick={() => removeAccount(a)} className="p-1.5 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4 text-red-400" /></button>
            </div>
            {a.iban && <p className="text-xs text-stone-400 font-mono mt-2 truncate">{a.iban}</p>}
            <div className="flex items-center justify-between mt-3">
              <span className="px-2 py-1 rounded-lg bg-stone-100 text-xs font-medium">{TYPE_LBL[a.account_type]}{a.commission_rate > 0 && ` • %${a.commission_rate} komisyon`}</span>
              <p className={`text-lg font-bold ${a.balance < 0 ? "text-red-600" : "text-stone-900"}`}>{tl(a.balance)} ₺</p>
            </div>
            <button data-testid={`bank-open-${a.account_id}`} onClick={() => openTxns(a)} className="mt-3 w-full border border-forest text-forest hover:bg-forest/5 text-sm font-semibold py-2 rounded-xl transition-colors">Hareketler & İşlem</button>
          </div>
        ))}
        {accounts.filter((a) => allowed.includes(a.account_type)).length === 0 && (
          <div className="sm:col-span-2 lg:col-span-3 bg-white rounded-2xl border border-dashed border-stone-300 p-10 text-center text-stone-400">
            <Landmark className="w-8 h-8 mx-auto mb-2 opacity-40" />Henüz hesap tanımlanmadı
          </div>
        )}
      </div>

      {form && (
        <div data-testid="bank-account-form-modal" className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setForm(null)}>
          <div className="bg-white rounded-2xl w-full max-w-2xl p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-serif text-xl text-forest">Yeni Hesap</h3>
              <button data-testid="bank-form-close" onClick={() => setForm(null)} className="p-1.5 hover:bg-stone-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {/* SOL: hesap kimliği */}
              <div className="space-y-3 sm:pr-6 sm:border-r sm:border-stone-200">
                <p className="text-[11px] uppercase tracking-wider font-bold text-stone-400">Hesap Bilgileri</p>
                <div><label className="text-xs font-semibold text-stone-600">Hesap Adı *</label><input data-testid="bank-form-name" className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="ör. Ziraat TL Hesabı" /></div>
                <div><label className="text-xs font-semibold text-stone-600">Banka</label><input className={inputCls} value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} /></div>
                <div><label className="text-xs font-semibold text-stone-600">IBAN</label><input className={inputCls} value={form.iban} onChange={(e) => setForm({ ...form, iban: e.target.value })} /></div>
              </div>
              {/* SAĞ: tür & mali */}
              <div className="space-y-3">
                <p className="text-[11px] uppercase tracking-wider font-bold text-stone-400">Tür & Mali</p>
                <div><label className="text-xs font-semibold text-stone-600">Hesap Türü</label>
                  <select data-testid="bank-form-type" className={inputCls} value={form.account_type} onChange={(e) => setForm({ ...form, account_type: e.target.value })}>
                    {typeOptions.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
                  </select></div>
                <div><label className="text-xs font-semibold text-stone-600">Komisyon Oranı % (POS/kredi kartı tahsilatlarında)</label>
                  <input data-testid="bank-form-commission" type="number" min="0" step="0.01" className={inputCls} value={form.commission_rate} onChange={(e) => setForm({ ...form, commission_rate: e.target.value })} /></div>
              </div>
            </div>
            <button data-testid="bank-account-submit" onClick={saveAccount} disabled={busy} className="mt-5 w-full bg-forest hover:bg-forest-dark disabled:bg-stone-400 text-white font-semibold py-3 rounded-xl transition-colors">{busy ? "Kaydediliyor..." : "Kaydet"}</button>
          </div>
        </div>
      )}

      {open && (
        <div data-testid="bank-txn-modal" className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setOpen(null)}>
          <div className="bg-white rounded-2xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <h3 className="font-serif text-xl text-forest">{open.account.name}</h3>
                <p className="text-2xl font-bold mt-1 text-stone-900" data-testid="bank-txn-balance">{tl(open.account.balance)} ₺</p>
              </div>
              <button data-testid="bank-txn-close" onClick={() => setOpen(null)} className="p-1.5 hover:bg-stone-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>

            <div className="flex flex-wrap gap-2 mb-4">
              <button data-testid="bank-stmt-excel" onClick={() => downloadStmt("xlsx")} className="flex items-center gap-1.5 text-xs font-semibold border border-forest text-forest hover:bg-forest/5 px-3.5 py-2 rounded-xl transition-colors"><FileSpreadsheet className="w-3.5 h-3.5" /> Excel</button>
              <button data-testid="bank-stmt-pdf" onClick={() => downloadStmt("pdf")} className="flex items-center gap-1.5 text-xs font-semibold bg-stone-800 hover:bg-stone-900 text-white px-3.5 py-2 rounded-xl transition-colors"><FileText className="w-3.5 h-3.5" /> PDF</button>
              <button data-testid="bank-stmt-print" onClick={printStmt} className="flex items-center gap-1.5 text-xs font-semibold border border-stone-300 text-stone-600 hover:bg-stone-50 px-3.5 py-2 rounded-xl transition-colors"><Printer className="w-3.5 h-3.5" /> Yazdır</button>
              <button data-testid="bank-stmt-wa" onClick={shareStmt} className="flex items-center gap-1.5 text-xs font-semibold bg-[#25D366] hover:bg-[#1fb857] text-white px-3.5 py-2 rounded-xl transition-colors"><MessageCircle className="w-3.5 h-3.5" /> WhatsApp</button>
              <button data-testid="bank-stmt-mail" onClick={mailStmt} className="flex items-center gap-1.5 text-xs font-semibold border border-stone-300 text-stone-600 hover:bg-stone-50 px-3.5 py-2 rounded-xl transition-colors"><Mail className="w-3.5 h-3.5" /> Mail</button>
              <button data-testid="bank-import-open" onClick={() => { setImpOpen(true); setImp(null); }} className="flex items-center gap-1.5 text-xs font-semibold bg-emerald-700 hover:bg-emerald-800 text-white px-3.5 py-2 rounded-xl transition-colors"><FileSpreadsheet className="w-3.5 h-3.5" /> Excel'den Aktar</button>
            </div>

            <div className="bg-stone-50 rounded-xl p-4 grid grid-cols-2 sm:grid-cols-5 gap-2 items-end">
              <div><label className="text-xs font-semibold text-stone-600">Tür</label>
                <select data-testid="bank-txn-type" className={inputCls} value={txn.type} onChange={(e) => setTxn({ ...txn, type: e.target.value })}>
                  <option value="tahsilat">Tahsilat</option>
                  <option value="odeme">Ödeme</option>
                  <option value="gider">Banka Gideri</option>
                  <option value="kredi_odeme">Kredi Ödemesi</option>
                </select></div>
              <div><label className="text-xs font-semibold text-stone-600">Tutar ₺</label><input data-testid="bank-txn-amount" type="number" min="0" step="0.01" className={inputCls} value={txn.amount} onChange={(e) => setTxn({ ...txn, amount: e.target.value })} /></div>
              <div><label className="text-xs font-semibold text-stone-600">Cari (ops.)</label>
                <select data-testid="bank-txn-cari" className={inputCls} value={txn.cari_id} onChange={(e) => setTxn({ ...txn, cari_id: e.target.value })}>
                  <option value="">—</option>
                  {caris.map((c) => <option key={c.cari_id} value={c.cari_id}>{c.name}</option>)}
                </select></div>
              <div><label className="text-xs font-semibold text-stone-600">Açıklama</label><input data-testid="bank-txn-desc" className={inputCls} value={txn.description} onChange={(e) => setTxn({ ...txn, description: e.target.value })} /></div>
              {["kredi_karti", "pos"].includes(open.account.account_type) && (
                <div><label className="text-xs font-semibold text-stone-600">Taksit</label>
                  <select data-testid="bank-txn-installments" className={inputCls} value={txn.installments} onChange={(e) => setTxn({ ...txn, installments: e.target.value })}>
                    <option value={1}>Tek Çekim</option>
                    {[2, 3, 4, 5, 6, 9, 12].map((n) => <option key={n} value={n}>{n} Taksit</option>)}
                  </select></div>
              )}
              <button data-testid="bank-txn-submit" onClick={submitTxn} disabled={busy} className="h-11 bg-forest hover:bg-forest-dark disabled:bg-stone-400 text-white text-sm font-semibold rounded-xl transition-colors">Ekle</button>
            </div>
            {commission > 0 && <p data-testid="bank-commission-preview" className="text-xs text-amber-700 mt-2">Komisyon: {tl(commission)} ₺ — net yansıyacak: {tl(parseFloat(txn.amount) - commission)} ₺</p>}

            <div data-testid="bank-txn-list" className="mt-5 divide-y divide-stone-100">
              {open.txns.map((t) => (
                <div key={t.txn_id} className="py-3 flex items-center gap-3">
                  {t.type === "tahsilat" ? <ArrowDownLeft className="w-4 h-4 text-emerald-600 shrink-0" /> : <ArrowUpRight className="w-4 h-4 text-red-500 shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-stone-800 truncate">{({ tahsilat: "Tahsilat", odeme: "Ödeme", gider: "Banka Gideri", kredi_odeme: "Kredi Ödemesi" })[t.type] || t.type} {t.cari_name && `• ${t.cari_name}`} {t.description && `• ${t.description}`}</p>
                    <p className="text-xs text-stone-400">{t.date} {t.installment_of > 1 && <span className="text-gold-dark font-semibold">• {t.installment_no}/{t.installment_of}. taksit </span>}{t.commission > 0 && `• komisyon ${tl(t.commission)} ₺, net ${tl(t.net)} ₺`}</p>
                  </div>
                  <span className={`text-sm font-bold ${t.type === "tahsilat" ? "text-emerald-700" : "text-red-600"}`}>{t.type === "tahsilat" ? "+" : "-"}{tl(t.amount)} ₺</span>
                  <button data-testid={`txn-edit-${t.txn_id}`} onClick={() => setEditTxn({ ...t, amount: String(t.amount) })} className="p-1.5 hover:bg-cream-dark rounded-lg" title="Düzenle"><Pencil className="w-3.5 h-3.5 text-stone-500" /></button>
                  <button data-testid={`txn-del-${t.txn_id}`} onClick={() => delTxn(t)} className="p-1.5 hover:bg-red-50 rounded-lg" title="Sil"><Trash2 className="w-3.5 h-3.5 text-red-400" /></button>
                </div>
              ))}
              {open.txns.length === 0 && <p className="py-6 text-center text-sm text-stone-400">Henüz işlem yok</p>}
            </div>
          </div>
        </div>
      )}

      {impOpen && (
        <div data-testid="bank-import-modal" className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" onClick={() => { setImpOpen(false); setImp(null); }}>
          <div className="bg-white rounded-2xl w-full max-w-2xl p-6 max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4"><h3 className="font-serif text-xl text-forest">Excel'den İşlem Aktar — {open?.account?.name}</h3><button data-testid="bank-import-close" onClick={() => { setImpOpen(false); setImp(null); }} className="p-1.5 hover:bg-stone-100 rounded-lg"><X className="w-5 h-5" /></button></div>
            <p className="text-xs text-stone-500 mb-3">Excel (.xlsx) veya CSV — kolonlar: <b>Tarih, Tür (Tahsilat/Ödeme), Tutar, Açıklama</b>. Tür boş bırakılırsa tutar işaretine göre belirlenir (pozitif = tahsilat). Mükerrer satırlar otomatik engellenir. <button type="button" data-testid="bank-import-template" onClick={() => downloadApi("/admin/import-templates/banka.xlsx", "banka-aktarim-sablonu.xlsx").catch(() => toast.error("Şablon indirilemedi"))} className="font-semibold text-forest underline">Şablon indir</button></p>
            <label className="flex items-center justify-center gap-2 border-2 border-dashed border-stone-300 rounded-xl p-6 cursor-pointer hover:border-forest transition-colors">
              <FileSpreadsheet className="w-5 h-5 text-stone-400" />
              <span className="text-sm text-stone-600">{impBusy ? "Okunuyor..." : "Dosya seç"}</span>
              <input data-testid="bank-import-file" type="file" accept=".xlsx,.csv" className="hidden" onChange={doImport} />
            </label>
            {imp && (
              <div className="mt-4 space-y-2">
                <p data-testid="bank-import-summary" className="text-sm font-semibold">{imp.new} yeni · {imp.duplicates} mükerrer</p>
                <div className="border border-stone-200 rounded-xl divide-y divide-stone-100 max-h-64 overflow-y-auto">
                  {imp.rows.map((r, i) => (
                    <div key={i} data-testid={`bank-import-row-${i}`} className="px-4 py-2 flex items-center justify-between text-sm gap-2">
                      <span className="truncate">{r.date} — {r.description}</span>
                      <span className="flex items-center gap-2 shrink-0">
                        <span className={`text-[10px] font-bold ${r.type === "tahsilat" ? "text-emerald-700" : "text-terra"}`}>{r.type === "tahsilat" ? "TAHSİLAT" : "ÖDEME"}</span>
                        <b>{tl(r.amount)} ₺</b>
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${r.status === "yeni" ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-700"}`}>{r.status === "yeni" ? "YENİ" : "MÜKERRER"}</span>
                      </span>
                    </div>
                  ))}
                </div>
                <button data-testid="bank-import-confirm" onClick={confirmImport} disabled={impBusy || imp.new === 0} className="w-full bg-forest hover:bg-forest-dark disabled:bg-stone-400 text-white font-semibold py-3 rounded-xl transition-colors">{impBusy ? "Aktarılıyor..." : `${imp.new} İşlemi Aktar`}</button>
              </div>
            )}
          </div>
        </div>
      )}

      {editTxn && (
        <div data-testid="bank-txn-edit-modal" className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" onClick={() => setEditTxn(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-serif text-xl text-forest">İşlem Düzenle</h3>
              <button data-testid="txn-edit-close" onClick={() => setEditTxn(null)} className="p-1.5 hover:bg-stone-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3">
              <div><label className="text-xs font-semibold text-stone-600">Tür</label>
                <select data-testid="txn-edit-type" className={inputCls} value={editTxn.type} onChange={(e) => setEditTxn({ ...editTxn, type: e.target.value })}>
                  <option value="tahsilat">Tahsilat</option><option value="odeme">Ödeme</option><option value="gider">Banka Gideri</option><option value="kredi_odeme">Kredi Ödemesi</option>
                </select></div>
              <div><label className="text-xs font-semibold text-stone-600">Tutar ₺</label><input data-testid="txn-edit-amount" type="number" min="0" step="0.01" className={inputCls} value={editTxn.amount} onChange={(e) => setEditTxn({ ...editTxn, amount: e.target.value })} /></div>
              <div><label className="text-xs font-semibold text-stone-600">Tarih</label><input data-testid="txn-edit-date" type="date" className={inputCls} value={editTxn.date || ""} onChange={(e) => setEditTxn({ ...editTxn, date: e.target.value })} /></div>
              <div><label className="text-xs font-semibold text-stone-600">Cari (ops.)</label>
                <select data-testid="txn-edit-cari" className={inputCls} value={editTxn.cari_id || ""} onChange={(e) => setEditTxn({ ...editTxn, cari_id: e.target.value })}>
                  <option value="">—</option>
                  {caris.map((c) => <option key={c.cari_id} value={c.cari_id}>{c.name}</option>)}
                </select></div>
              <div><label className="text-xs font-semibold text-stone-600">Açıklama</label><input data-testid="txn-edit-desc" className={inputCls} value={editTxn.description || ""} onChange={(e) => setEditTxn({ ...editTxn, description: e.target.value })} /></div>
            </div>
            <p className="text-[11px] text-stone-400 mt-3">Bakiye farkı ve bağlı cari hareketi otomatik düzeltilir. Taksitli işlemlerde yalnızca bu taksit satırı etkilenir.</p>
            <button data-testid="txn-edit-save" onClick={saveEditTxn} disabled={busy} className="mt-4 w-full bg-forest hover:bg-forest-dark disabled:bg-stone-400 text-white font-semibold py-3 rounded-xl transition-colors">{busy ? "Kaydediliyor..." : "Kaydet"}</button>
          </div>
        </div>
      )}
    </div>
  );
}
